import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppTheme from "../../../theme/AppTheme";
import { dataGridCustomizations } from "../../../theme/customizations";
import {
  Box,
  Button,
  Chip,
  CssBaseline,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { notifyError, notifySuccess } from "../../../components/ToastContainer/Toast/Toast";
import {
  AuditPage,
  exportWalletAudit,
  getWalletAudit,
  getWalletAuditOrders,
  getWalletAuditSummary,
  getWalletAuditTopups,
  WalletAuditFilters,
  WalletAuditRow,
  WalletAuditSummary,
} from "../../../service/BuyerConfiguration/Wallets/walletService";

const inr = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : `₹${Number(n).toFixed(2)}`;

const when = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** Ledger types, in the words the audit log uses. */
const TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "TOPUP", label: "Money added" },
  { value: "ORDER_PAYMENT", label: "Paid / on hold for order" },
  { value: "REFUND", label: "Refund to wallet" },
  { value: "PARTIAL_REFUND", label: "Refund for cancelled items" },
  { value: "CANCELLATION_REFUND", label: "Refund for cancelled order" },
  { value: "CASHBACK", label: "Cashback" },
  { value: "PROMO_CREDIT", label: "Promo code" },
  { value: "COMPENSATION", label: "Goodwill credit" },
  { value: "REFERRAL", label: "Referral reward" },
  { value: "EXPIRY", label: "Expired" },
  { value: "ADMIN_CREDIT", label: "Added by Tazty" },
  { value: "ADMIN_DEBIT", label: "Adjusted by Tazty" },
  { value: "REVERSAL", label: "Correction" },
];

const TABS = ["activity", "topups", "orders"] as const;
type TabKey = (typeof TABS)[number];

const STATUS_BY_TAB: Record<TabKey, string[]> = {
  activity: ["completed", "held", "released"],
  topups: ["created", "paid", "credited", "failed", "expired"],
  orders: ["held", "captured", "released", "shortfall"],
};

const statusColor = (s: string): "default" | "success" | "warning" | "error" | "info" => {
  if (["completed", "credited", "captured", "paid"].includes(s)) return "success";
  if (["held", "created"].includes(s)) return "warning";
  if (["failed", "shortfall"].includes(s)) return "error";
  if (["released", "expired"].includes(s)) return "default";
  return "info";
};

const csvCell = (v: unknown) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Wallet Audit Log - every rupee that moved in or out of any customer's Tazty
 * Wallet, every top-up attempt and every wallet-paid order. Read-only: nothing
 * on this page changes a balance. Super Admin only (same as Wallets).
 */
const WalletAuditLog = (props: { disableCustomTheme?: boolean }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("activity");
  const [filters, setFilters] = useState<WalletAuditFilters>({});
  const [paging, setPaging] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });
  const [summary, setSummary] = useState<WalletAuditSummary | null>(null);
  const [data, setData] = useState<AuditPage<any> | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const setFilter = (patch: Partial<WalletAuditFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPaging((p) => ({ ...p, page: 0 }));
  };

  const query = useMemo(
    () => ({ ...filters, page: paging.page + 1, limit: paging.pageSize }),
    [filters, paging],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fetchTab =
        tab === "activity" ? getWalletAudit : tab === "topups" ? getWalletAuditTopups : getWalletAuditOrders;
      const [rows, sum] = await Promise.all([fetchTab(query), getWalletAuditSummary(filters)]);
      setData(rows);
      setSummary(sum);
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not load the wallet audit log");
    } finally {
      setLoading(false);
    }
  }, [tab, query, filters]);

  // Typing in a filter waits a moment before asking the server.
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [load]);

  const openCustomer = (userId: number) => navigate(`/wallets?user=${userId}`);

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const res = await exportWalletAudit(filters);
      const header = [
        "Date", "Customer", "Phone", "Type", "Direction", "Amount", "Bucket", "Status",
        "Balance after", "Order", "Payment ref", "Reason", "Done by", "Txn ref",
      ];
      const lines = res.items.map((r: WalletAuditRow) =>
        [
          when(r.created_at), r.customer_name, r.customer_phone, r.label,
          r.direction === "credit" ? "IN" : "OUT", r.amount.toFixed(2), r.bucket, r.status,
          r.balance_after ?? "", r.order_number, r.payment_ref, r.reason, r.created_by, r.txn_ref,
        ].map(csvCell).join(","),
      );
      const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tazty-wallet-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      notifySuccess(
        res.truncated
          ? `Downloaded the first ${res.max} rows - narrow the dates for the rest`
          : `Downloaded ${res.items.length} rows`,
      );
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not download the audit log");
    } finally {
      setExporting(false);
    }
  };

  const customerCol: GridColDef = {
    field: "customer",
    headerName: "Customer",
    minWidth: 200,
    flex: 1,
    sortable: false,
    renderCell: (p) => (
      <Box
        sx={{ cursor: "pointer", lineHeight: 1.3, py: 0.5 }}
        onClick={() => p.row.user_id && openCustomer(p.row.user_id)}
        title="Open this customer's wallet"
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
          {p.row.customer_name || "—"}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {p.row.customer_phone || ""}
        </Typography>
      </Box>
    ),
  };
  const statusCol: GridColDef = {
    field: "status",
    headerName: "Status",
    width: 120,
    renderCell: (p) => <Chip size="small" label={p.row.status} color={statusColor(p.row.status)} variant="outlined" />,
  };
  const whenCol: GridColDef = {
    field: "created_at",
    headerName: "Date & time",
    width: 175,
    valueGetter: (_v, r) => when(r.created_at),
  };

  const columns: GridColDef[] =
    tab === "activity"
      ? [
          whenCol,
          customerCol,
          { field: "label", headerName: "Type", minWidth: 190, flex: 1 },
          {
            field: "amount",
            headerName: "Amount",
            width: 120,
            renderCell: (p) => {
              // An order hold is not final: shown without +/- until settled.
              const hold = p.row.type === "ORDER_PAYMENT" && p.row.status !== "completed";
              const sign = hold ? "" : p.row.direction === "credit" ? "+" : "-";
              const color = hold ? "text.secondary" : p.row.direction === "credit" ? "success.main" : "error.main";
              return (
                <Typography variant="body2" sx={{ fontWeight: 700, color }}>
                  {sign}
                  {inr(p.row.amount)}
                </Typography>
              );
            },
          },
          { field: "bucket", headerName: "Bucket", width: 95 },
          statusCol,
          { field: "balance_after", headerName: "Balance after", width: 120, valueGetter: (_v, r) => inr(r.balance_after) },
          { field: "order_number", headerName: "Order", width: 200, valueGetter: (_v, r) => r.order_number || "—" },
          { field: "payment_ref", headerName: "Payment ref", width: 170, valueGetter: (_v, r) => r.payment_ref || "—" },
          {
            field: "reason",
            headerName: "Reason",
            minWidth: 200,
            flex: 1,
            renderCell: (p) =>
              p.row.reason ? <span title={p.row.reason}>{p.row.reason}</span> : <span style={{ opacity: 0.5 }}>—</span>,
          },
          { field: "created_by", headerName: "Done by", width: 150 },
        ]
      : tab === "topups"
        ? [
            whenCol,
            customerCol,
            { field: "amount", headerName: "Amount", width: 110, valueGetter: (_v, r) => inr(r.amount) },
            { field: "fee_amount", headerName: "Fee", width: 90, valueGetter: (_v, r) => inr(r.fee_amount) },
            { field: "fee_bearer", headerName: "Fee paid by", width: 110 },
            { field: "charged_amount", headerName: "Charged", width: 110, valueGetter: (_v, r) => inr(r.charged_amount) },
            { field: "bonus_amount", headerName: "Bonus", width: 90, valueGetter: (_v, r) => (r.bonus_amount ? inr(r.bonus_amount) : "—") },
            statusCol,
            { field: "failure_reason", headerName: "Failure reason", minWidth: 180, flex: 1, valueGetter: (_v, r) => r.failure_reason || "—" },
            { field: "razorpay_payment_id", headerName: "Razorpay payment", width: 190, valueGetter: (_v, r) => r.razorpay_payment_id || "—" },
            { field: "razorpay_order_id", headerName: "Razorpay order", width: 190 },
          ]
        : [
            whenCol,
            customerCol,
            { field: "order_number", headerName: "Order", width: 210 },
            { field: "total_amount", headerName: "Order total", width: 120, valueGetter: (_v, r) => inr(r.total_amount) },
            { field: "wallet_amount", headerName: "From wallet", width: 120, valueGetter: (_v, r) => inr(r.wallet_amount) },
            { field: "online_amount", headerName: "Paid online", width: 120, valueGetter: (_v, r) => inr(r.online_amount) },
            {
              field: "wallet_status",
              headerName: "Wallet part",
              width: 130,
              renderCell: (p) =>
                p.row.wallet_status ? (
                  <Chip size="small" label={p.row.wallet_status} color={statusColor(p.row.wallet_status)} variant="outlined" />
                ) : (
                  "—"
                ),
            },
            { field: "payment_status", headerName: "Payment", width: 110 },
            { field: "status", headerName: "Order status", width: 130 },
          ];

  const card = (label: string, value: number | string, hint?: string) => (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 150 }}>
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        {label}
      </Typography>
      <Typography variant="h6">{typeof value === "number" ? inr(value) : value}</Typography>
      {hint ? (
        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );

  return (
    <AppTheme {...props} themeComponents={{ ...dataGridCustomizations }}>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" }, p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2, alignItems: { sm: "center" }, justifyContent: "space-between" }}
        >
          <Box>
            <Typography variant="h5">Wallet Audit Log</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Every rupee in and out of every customer's Tazty Wallet. Read-only.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => navigate("/wallets")}>
              Back to wallets
            </Button>
            <Button variant="contained" onClick={downloadCsv} disabled={exporting || tab !== "activity"}>
              {exporting ? "Preparing…" : "Download CSV"}
            </Button>
          </Stack>
        </Stack>

        {summary && (
          <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1.5 }} useFlexGap>
            {card("Money added", summary.money_added)}
            {card("Spent on orders", summary.spent_on_orders)}
            {card("Refunded to wallet", summary.refunded_to_wallet)}
            {card("Promo & cashback", summary.promo_and_cashback)}
            {card("Returned (order not paid)", summary.returned_unpaid)}
            {card("On hold now (in filter)", summary.on_hold)}
            {card("Expired", summary.expired)}
            {card("Admin +/−", `${inr(summary.admin_credit)} / ${inr(summary.admin_debit)}`)}
            {card("Tazty owes customers now", summary.owed_now, `${summary.wallets} wallets · not filtered`)}
          </Stack>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={filters.from || ""}
            onChange={(e) => setFilter({ from: e.target.value || undefined })}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={filters.to || ""}
            onChange={(e) => setFilter({ to: e.target.value || undefined })}
          />
          <TextField
            size="small"
            label="Customer phone or name"
            value={filters.search || ""}
            onChange={(e) => setFilter({ search: e.target.value })}
          />
          {tab === "activity" && (
            <>
              <TextField
                select
                size="small"
                label="Type"
                sx={{ minWidth: 200 }}
                value={filters.type || ""}
                onChange={(e) => setFilter({ type: e.target.value || undefined })}
              >
                <MenuItem value="">All types</MenuItem>
                {TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="In / out"
                sx={{ minWidth: 120 }}
                value={filters.direction || ""}
                onChange={(e) => setFilter({ direction: (e.target.value || undefined) as any })}
              >
                <MenuItem value="">Both</MenuItem>
                <MenuItem value="credit">Money in</MenuItem>
                <MenuItem value="debit">Money out</MenuItem>
              </TextField>
              <TextField
                size="small"
                label="Done by"
                value={filters.created_by || ""}
                onChange={(e) => setFilter({ created_by: e.target.value })}
              />
            </>
          )}
          {tab !== "topups" && (
            <TextField
              size="small"
              label="Order number"
              value={filters.order_number || ""}
              onChange={(e) => setFilter({ order_number: e.target.value })}
            />
          )}
          <TextField
            select
            size="small"
            label="Status"
            sx={{ minWidth: 130 }}
            value={filters.status || ""}
            onChange={(e) => setFilter({ status: e.target.value || undefined })}
          >
            <MenuItem value="">Any</MenuItem>
            {STATUS_BY_TAB[tab].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <Button onClick={() => { setFilters({}); setPaging((p) => ({ ...p, page: 0 })); }}>Clear</Button>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_e, v: TabKey) => {
            setTab(v);
            setFilters((f) => ({ ...f, status: undefined, type: undefined, direction: undefined }));
            setPaging((p) => ({ ...p, page: 0 }));
          }}
          sx={{ mb: 1 }}
        >
          <Tab value="activity" label="All wallet activity" />
          <Tab value="topups" label="Top-up attempts" />
          <Tab value="orders" label="Wallet orders" />
        </Tabs>

        <DataGrid
          rows={data?.items ?? []}
          columns={columns}
          loading={loading}
          getRowId={(r: any) => `${tab}-${r.id}`}
          autoHeight
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={data?.pagination?.total ?? 0}
          paginationModel={paging}
          onPaginationModelChange={setPaging}
          pageSizeOptions={[25, 50, 100]}
          getRowHeight={() => "auto"}
          sx={{ "& .MuiDataGrid-cell": { py: 0.5, alignItems: "center", display: "flex" } }}
        />
      </Box>
    </AppTheme>
  );
};

export default WalletAuditLog;
